import { backendServices } from './backend.service';

const BASE = '/admin/reports';

/** Active assets from VESTN (for report asset selection). */
export interface ActiveAssetOption {
  id: string;
  assetName: string;
  symbol?: string;
  status?: 'PENDING' | 'APPROVED' | 'REJECTED';
  isListedForResell?: boolean;
  saleEndDate?: string;
  source: 'asset';
}

/** Rental listings for report asset selection. */
export interface RentalOption {
  id: string;
  assetName: string;
  status?: 'AVAILABLE' | 'RENTED';
  source: 'rental';
}

/** Union type for assets + rentals in the report form. */
export type AssetOrRentalOption = ActiveAssetOption | RentalOption;

interface AssetListItem {
  _id: string;
  assetName?: string;
  symbol?: string;
  status?: string;
  isListedForResell?: boolean;
  saleEndDate?: string;
}

interface AssetListApiResponse {
  status: boolean;
  data?: AssetListItem[];
}

export interface ReportTemplate {
  id: string;
  name: string;
  type: string;
  description?: string;
  schedule: string;
  format: string;
  metrics?: string[];
  assets?: string[];
  dateRange?: string;
  recipients?: string;
  includeCharts?: boolean;
  lastGenerated: string;
  lastGeneratedAt?: string;
}

export interface ReportCreatePayload {
  name: string;
  type: string;
  description?: string;
  schedule?: string;
  format?: string;
  metrics?: string[];
  assets?: string[];
  dateRange?: string;
  recipients?: string;
  includeCharts?: boolean;
}

interface ApiResponse<T = unknown> {
  message: string;
  status: boolean;
  data?: T;
  error?: string;
}

async function handleResponse<T>(response: { data?: ApiResponse<T>; status?: number }): Promise<T> {
  const data = response.data;
  if (data?.status) {
    return data.data as T;
  }
  if (response.status === 401) {
    throw new Error('Authentication required. Please log in again.');
  }
  if (response.status === 403) {
    throw new Error('Access denied. Admin privileges required.');
  }
  const errData = data as { error?: string; message?: string } | undefined;
  throw new Error(errData?.error ?? errData?.message ?? 'Request failed');
}

async function getActiveAssetsFromApi(): Promise<ActiveAssetOption[]> {
  const res = await backendServices.get<AssetListApiResponse>('/asset?page=1&limit=500');
  const data = res.data;
  if (!data?.status || !Array.isArray(data.data)) return [];
  return data.data.map((a) => ({
    id: a._id,
    assetName: a.assetName || 'Unnamed Asset',
    symbol: a.symbol,
    status: a.status as ActiveAssetOption['status'],
    isListedForResell: a.isListedForResell,
    saleEndDate: a.saleEndDate,
    source: 'asset' as const,
  }));
}

interface RentalListItem {
  _id: string;
  status?: string;
  assetId?: { _id: string; assetName?: string };
}

interface RentalListApiResponse {
  status: boolean;
  data?: RentalListItem[];
}

async function getActiveRentalsFromApi(): Promise<RentalOption[]> {
  const res = await backendServices.get<RentalListApiResponse>(
    '/rental/getRentalList?page=1&limit=500'
  );
  const data = res.data;
  if (!data?.status || !Array.isArray(data.data)) return [];
  return data.data.map((r) => ({
    id: r._id,
    assetName: r.assetId?.assetName || 'Rental',
    status: r.status as RentalOption['status'],
    source: 'rental' as const,
  }));
}

export const reportApi = {
  /** Fetch currently active assets for report form dropdown/pills. */
  getActiveAssets: getActiveAssetsFromApi,
  /** Fetch rental listings for report form. */
  getActiveRentals: getActiveRentalsFromApi,

  async list(): Promise<ReportTemplate[]> {
    const res = await backendServices.get<ApiResponse<ReportTemplate[]>>(BASE);
    return handleResponse<ReportTemplate[]>(res);
  },

  async getOne(id: string): Promise<ReportTemplate> {
    const res = await backendServices.get<ApiResponse<ReportTemplate>>(`${BASE}/${id}`);
    return handleResponse<ReportTemplate>(res);
  },

  async create(payload: ReportCreatePayload): Promise<ReportTemplate> {
    const res = await backendServices.post<ApiResponse<ReportTemplate>>(BASE, payload);
    return handleResponse<ReportTemplate>(res);
  },

  async update(id: string, payload: ReportCreatePayload): Promise<ReportTemplate> {
    const res = await backendServices.put<ApiResponse<ReportTemplate>>(`${BASE}/${id}`, payload);
    return handleResponse<ReportTemplate>(res);
  },

  async delete(id: string): Promise<void> {
    const res = await backendServices.delete<ApiResponse<void>>(`${BASE}/${id}`);
    handleResponse<void>(res);
  },

  async generate(id: string): Promise<Record<string, unknown>> {
    const res = await backendServices.post<ApiResponse<Record<string, unknown>>>(
      `${BASE}/${id}/generate`,
      {}
    );
    return handleResponse<Record<string, unknown>>(res);
  },

  async download(id: string): Promise<Blob> {
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = {};
    if (token) headers['x-auth-token'] = token;
    const { api } = await import('../config');
    const now = new Date().toISOString();
    const response = await api.get(`${BASE}/${id}/download?now=${encodeURIComponent(now)}`, {
      headers,
      responseType: 'blob',
    });
    if (response.status !== 200) {
      const text = await (response.data as Blob).text();
      let message = 'Download failed';
      try {
        const json = JSON.parse(text);
        message = (json as { message?: string }).message || message;
      } catch {
        // use default message
      }
      throw new Error(message);
    }
    return response.data as Blob;
  },
};
